import {
  Clear as ClearIcon,
  DeleteRounded as DeleteRoundedIcon,
  EditRounded as EditRoundedIcon,
  PlaceRounded as PlaceRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
  RestartAltRounded as RestartAltRoundedIcon,
  RssFeedRounded as RssFeedRoundedIcon,
  SendRounded as SendRoundedIcon
} from '@mui/icons-material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tab,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { typeToString } from '../../../helpers/lorawanFieldsHelper.js'
import userPermission from '../../../helpers/userPermission.js'
import { useEvents } from '../../Events/Contexts/EventsContext.jsx'
import Can from '../../Layout/Can/Can.jsx'
import { useAuth } from '../../Shared/Contexts/AuthContext.jsx'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import ConfirmWithCheckDialog from '../../Shared/Dialogs/ConfirmWithCheckDialog.jsx'
import { useDevices } from '../Contexts/DevicesContext.jsx'
import Actions from '../Dialogs/Actions.jsx'
import SendAction from '../Dialogs/SendAction.jsx'
import UpdateDevice from '../Dialogs/UpdateDevice.jsx'
import LifecycleBadge from '../StatusBadge/LifecycleBadge.jsx'
import DeviceStatusBadge from '../StatusBadge/StatusBadge.jsx'
import EventsTab from '../Tabs/EventsTab.jsx'
import MeasuresTab from '../Tabs/MeasuresTab.jsx'
import NetworkTab from '../Tabs/NetworkTab.jsx'
import PositionHistoryMap from './PositionHistoryMap.jsx'

const EVENTS_PAGE_SIZE = 100

// keep in sync with FrontPage.jsx returnPage map
const returnPage = {
  devicesList: '/management/devices',
  eventsList: '/management/events'
}

const DeviceDetail = ({ deviceId, onDeviceSelected }) => {
  const { setMap, openErrorAlert, dateRange } = useGlobalContext()
  const {
    authState: { isAuthenticated },
    currentOrganizationKey,
    currentUser: { isSuperAdmin, selfOrganizations }
  } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const canReadDevices = userPermission('devices_r')
  const { useFetchDevice, useDeleteDevice, useHardDeleteDevice, useRestoreDevice, useFetchDeviceMeasurements, useFetchDevicePositions, useFetchDeviceNetwork, useFetchDeviceStatistics } = useDevices()
  const { useFetchEvents, useFetchEventsSearchSummary } = useEvents()

  const [organization, setOrganization] = useState(null)
  const [activeTab, setActiveTab] = useState('measures')
  const [isEditDeviceDialogOpen, setIsEditDeviceDialogOpen] = useState(false)
  const [shouldRefreshGraph, setShouldRefreshGraph] = useState(true)
  const [isConfirmWithCheckOpen, setIsConfirmWithCheckOpen] = useState(false)
  const [isHardDeleteConfirmOpen, setIsHardDeleteConfirmOpen] = useState(false)
  const [device, setDevice] = useState(null)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
  const [isSendActionOpen, setIsSendActionOpen] = useState(false)
  const [error, setError] = useState(null)
  const [measuresData, setMeasuresData] = useState(false)
  const [disableRefresh, setDisableRefresh] = useState(false)
  const [secondsToWait, setSecondsToWait] = useState(30)
  const [events, setEvents] = useState()
  const [eventsPage, setEventsPage] = useState(1)
  const [allEvents, setAllEvents] = useState()
  const [networkChart, setNetworkChart] = useState()
  const [statisticsChart, setStatisticsChart] = useState()
  const [positions, setPositions] = useState([])
  const [isPositionsLoading, setIsPositionsLoading] = useState(false)
  const [positionsRefreshKey, setPositionsRefreshKey] = useState(0)
  const isDisabled = device?.device?.status === 'disabled'

  const handleEdit = () => {
    if (!isDisabled) setIsEditDeviceDialogOpen(true)
  }

  const handleDelete = () => {
    if (!isDisabled) setIsConfirmWithCheckOpen(true)
  }

  const handleConfirmHardDelete = () => {
    useHardDeleteDevice({ id: device.device.id })
      .then(() => navigate(returnPage[returnTo] ?? '/'))
      .catch((err) => openErrorAlert({ keys: err?.data?.keys || ['general'] }))
    setIsHardDeleteConfirmOpen(false)
  }

  const handleRestore = () => {
    useRestoreDevice({ id: device.device.id })
      .then(() => useFetchDevice({ id: deviceId }).then((res) => setDevice(res.data)))
      .catch((err) => openErrorAlert({ keys: err?.data?.keys || ['general'] }))
  }

  const handleRefreshGraphs = () => {
    setDisableRefresh(true)
    setShouldRefreshGraph(true)
    setPositionsRefreshKey((key) => key + 1)
  }

  const handleSetIsOpenEditDevice = (isOpen) => setIsEditDeviceDialogOpen(isOpen)

  const handleCenterMap = ({ device }) => {
    onDeviceSelected({ id: device.id })
    setMap({
      lat: device.position?.latitude,
      lng: device.position?.longitude,
      zoom: 20
    })
  }

  const handleChangeTab = (_, activeTab) => setActiveTab(activeTab)

  const handleSetIsOpenActionDialogOpen = (isOpen) => setIsActionDialogOpen(isOpen)

  const handleSendAction = () => {
    setIsActionDialogOpen(false)
    setIsSendActionOpen(true)
  }

  const handleAction = () => {
    if (!isDisabled) setIsActionDialogOpen(true)
  }

  const handleSendActionIsOpen = (isOpen) => setIsSendActionOpen(isOpen)

  const handleCloseConfigrWithCheck = () => setIsConfirmWithCheckOpen(false)

  const handleConfirmAction = () => {
    if (isDisabled) return

    useDeleteDevice({ id: device.device.id })
      .then(() => {
        navigate(returnPage[returnTo] ?? '/')
      })
      .catch((err) => openErrorAlert({ keys: err.data.keys }))
    setIsConfirmWithCheckOpen(false)
  }

  useEffect(() => {
    if (!disableRefresh) {
      setSecondsToWait(30)
      return
    }

    const timeoutId = setTimeout(() => {
      setDisableRefresh(false)
    }, 30000)

    const intervalId = setInterval(() => setSecondsToWait(prev => {
      return prev - 1
    }), 1000)

    return () => {
      clearTimeout(timeoutId)
      clearInterval(intervalId)
    }
  }, [disableRefresh])

  useEffect(() => {
    setOrganization(selfOrganizations.find((o) => o?.key === device?.device?.organizationKey))
  }, [device?.device?.organizationKey])

  useEffect(() => { setShouldRefreshGraph(true) }, [dateRange])

  useEffect(() => {
    setEventsPage((currentPage) => currentPage === 1 ? currentPage : 1)
  }, [device?.device?.id, dateRange])

  useEffect(() => {
    if (!shouldRefreshGraph || device == null) {
      return
    }

    const visibleQuantities = device.quantities.filter((q) => !q.hidden)

    useFetchDeviceMeasurements({
      id: device.device.id,
      measureNames: visibleQuantities.map((q) => q.measureName),
      from: dateRange.from,
      to: dateRange.to
    })
      .then((res) => setMeasuresData(res.data))

    setShouldRefreshGraph(false)
  }, [device, shouldRefreshGraph, dateRange])

  useEffect(() => {
    if (!shouldRefreshGraph || device == null) {
      return
    }

    useFetchEventsSearchSummary({
      fromTo: {
        from: dateRange.from,
        to: dateRange.to
      },
      deviceIds: [device.device.id]
    }).then((res) => setAllEvents(res))

    useFetchEvents({
      from: dateRange.from,
      page: eventsPage,
      pageSize: EVENTS_PAGE_SIZE,
      to: dateRange.to,
      deviceId: device.device.id
    })
      .then(setEvents)

    setShouldRefreshGraph(false)
  }, [device, shouldRefreshGraph])

  useEffect(() => {
    if (device == null) {
      return
    }

    useFetchEvents({
      from: dateRange.from,
      page: eventsPage,
      pageSize: EVENTS_PAGE_SIZE,
      to: dateRange.to,
      deviceId: device.device.id
    })
      .then(setEvents)
  }, [device?.device?.id, dateRange, eventsPage])

  useEffect(() => {
    if (!shouldRefreshGraph || device == null) {
      return
    }
    useFetchDeviceNetwork({
      id: device.device.id,
      from: dateRange.from,
      to: dateRange.to
    })
      .then((res) => setNetworkChart(res.data))

    useFetchDeviceStatistics({
      id: device.device.id,
      from: dateRange.from,
      to: dateRange.to
    })
      .then((res) => setStatisticsChart(res.data))
    setShouldRefreshGraph(false)

  }, [device, shouldRefreshGraph])

  const trackerId = device?.device?.type === 'Tracker' ? device.device.id : null

  useEffect(() => {
    let isCurrent = true

    setPositions([])

    if (!trackerId) {
      setIsPositionsLoading(false)
      return
    }

    setIsPositionsLoading(true)
    useFetchDevicePositions({ id: trackerId, from: dateRange.from, to: dateRange.to })
      .then((res) => {
        if (isCurrent) setPositions(Array.isArray(res?.data) ? res.data : [])
      })
      .catch(() => {
        if (isCurrent) setPositions([])
      })
      .finally(() => {
        if (isCurrent) setIsPositionsLoading(false)
      })

    return () => {
      isCurrent = false
    }
  }, [trackerId, dateRange.from, dateRange.to, positionsRefreshKey, currentOrganizationKey, isAuthenticated])

  const handleFetchError = (err) => {
    const status = err.status
    const data = err.data
    switch (status) {
      case 422:
        setError(t('devices.errors.invalidId'))
        break
      case 404:
        if (data?.keys && Array.isArray(data.keys) && data.keys.length > 0) {
          const errorMessage = data.keys.map((key) => t(`devices.errors.${key}`)).join(', ')
          setError(errorMessage)
        } else {
          setError(t('devices.errors.deviceId'))
        }
        break
      default:
        setError(t('common.genericRequestError'))
        break
    }
  }

  useEffect(() => {
    setError(null)
    setDevice(null)

    useFetchDevice({ id: deviceId })
      .then((res) => {
        setDevice(res.data)
        res.data.device.position?.latitude && res.data.device.position?.longitude
          ? setMap({
            lat: res.data.device.position.latitude,
            lng: res.data.device.position.longitude,
            zoom: 20
          })
          : setMap({
            lat: 0,
            lng: 0,
            zoom: 3
          })
      })
      .catch(handleFetchError)
  }, [deviceId])

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography
          variant="h4"
          color="text.secondary"
          align="center"
          sx={{ whiteSpace: 'pre-line' }}>
          {error}
        </Typography>
      </Box>
    )
  }

  if (!device) {
    return null
  }

  const showNetworkTab = canReadDevices && device.sourceData && ['lorawan', 'wmbus', 'nbiot'].includes(device.device.source.type)

  return (
    <>
      <Stack
        direction="row"
        mb={3}
        justifyContent={'flex-start'}>
        <DeviceStatusBadge
          isAlive={device?.device?.isAlive}
          isAlarm={device?.device?.isAlarm}
        />
        <LifecycleBadge status={device?.device?.status} />
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems={'flex-end'}
          width="100%"
          px={1}>
          <Box>
            <ButtonGroup size="small">
              {!isDisabled && <Can action="devices_d">
                <IconButton
                  id="device-detail-delete"
                  key="delete"
                  onClick={handleDelete}
                  aria-label={`${t('devices.delete')}`}
                  color="error">
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Can>}
              {isDisabled && isSuperAdmin && (
                <IconButton
                  id="device-detail-restore"
                  key="restore"
                  onClick={() => handleRestore(device?.device)}
                  aria-label={`${t('devices.restore')} ${device?.device?.name}`}
                  color="success">
                  <RestartAltRoundedIcon fontSize="small" />
                </IconButton>
              )}
              {isSuperAdmin && (
                <IconButton
                  id="device-detail-hard-delete"
                  key="hard-delete"
                  onClick={() => setIsHardDeleteConfirmOpen(true)}
                  aria-label={`${t('devices.hardDelete')} ${device?.device?.name}`}
                  color="error">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              {!isDisabled && <Can action="devices_u">
                <IconButton
                  id="device-detail-actions"
                  key="actions"
                  disabled={!device?.actions?.length}
                  onClick={handleAction}
                  aria-label={`${t('devices.actionsDevice.title')}`}
                  color="primary">
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              </Can>}
              {!isDisabled && <Can action="devices_u">
                <IconButton
                  id="device-detail-edit"
                  key="edit"
                  aria-label={`${t('devices.update')}`}
                  color="primary"
                  onClick={() => handleEdit(device)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Can>}
            </ButtonGroup>
          </Box>
        </Box>
      </Stack>

      <Divider />

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        mt={5}>
        <Stack spacing={1}>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ textTransform: 'uppercase' }}>
            {organization?.name}
          </Typography>
          <Typography
            variant="h2"
            onClick={() => handleCenterMap(device)}
            id={'device-detail-name'}>
            {device?.device?.name}
          </Typography>
          <Typography
            variant="body"
            color="text.secondary"
            sx={{ textTransform: 'uppercase' }}>
            {device?.device?.brand} {device?.device?.model ? '/' : ''} {device?.device?.model}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        mt={5}>
        {device?.device?.position?.latitude && device?.device?.position?.longitude && (
          <Card
            variant="standard"
            sx={{
              width: '100%',
              minHeight: '140px'
            }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between">
                <PlaceRoundedIcon fontSize="small" />
              </Stack>
              <Typography
                mt={3}
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 500
                }}>
                {device?.device?.address}
              </Typography>
              <Typography sx={{
                fontSize: '0.8rem',
                fontWeight: 200
              }}>
                {device?.device?.position?.latitude}, {device?.device?.position?.longitude}
              </Typography>
            </CardContent>
          </Card>
        )}

        {(!device?.device?.position?.latitude || !device?.device?.position?.longitude) && (
          <Card
            variant="standard"
            sx={{
              width: '100%',
              minHeight: '140px',
              background: 'none',
              border: '1px solid red'
            }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="25vh">
                <Box>
                  <Typography
                    variant="h4"
                    align="center">
                    {t('common.warning')}
                  </Typography>
                  <Typography
                    variant="h6"
                    align="center">
                    {t('devices.noCoordinates')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card
          variant="standard"
          sx={{
            width: '100%',
            minHeight: '140px'
          }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              {device?.sourceData &&
                <Typography
                  sx={{ fontSize: '0.7rem' }}
                  color={device?.sourceData.device.info.joined ? 'success' : '#ef5350'}>
                  <RssFeedRoundedIcon fontSize="small" /> {device?.sourceData.device.info.joined ? 'Joined' : 'Not joined'}
                </Typography>
              }
              <Typography sx={{
                fontSize: '0.7rem',
                fontWeight: 200
              }}>
                {t('devices.added')} {device?.device?.createdAt && moment(device.device.createdAt).format('DD.MM.YYYY HH:mm:ss')}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              <Box>
                <Typography
                  mt={3}
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}>
                  {t('devices.lastUpdate')}
                </Typography>
                <Typography sx={{
                  fontSize: '0.8rem',
                  fontWeight: 200
                }}>
                  {device?.device?.lastMeasurementAt && moment(device.device.lastMeasurementAt).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  mt={3}
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}>
                  {t('devices.activeSince')}
                </Typography>
                <Typography sx={{
                  fontSize: '0.8rem',
                  fontWeight: 200
                }}>
                  {device?.device?.activeSince && moment(device.device.activeSince).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Typography
        mt={5}
        mb={2}
        variant="title">
        {t('devices.description')}
      </Typography>
      <Typography variant="body2">{device?.device?.description}</Typography>

      <Typography
        mt={5}
        mb={2}
        variant="title">
        {t('devices.serial')}
      </Typography>
      <Typography variant="body2">{device?.device?.serial}</Typography>

      {device?.sourceData && (
        <Box sx={{ marginTop: '30px' }}>
          <Typography
            mt={5}
            mb={2}
            variant="title">
            {t('devices.deviceInformation')}
          </Typography>
          <Stack
            sx={{ marginTop: '15px' }}
            spacing={3}>
            <Stack
              direction={'row'}
              spacing={15}>
              {typeToString(device?.sourceData.device.type) == 'OTAA' ?
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    variant="p">
                    AppKey
                  </Typography>
                  <Typography variant="body2">{device?.sourceData.device.appKey}</Typography>
                </Stack>
                :
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    variant="p">
                    AppSKey
                  </Typography>
                  <Typography variant="body2">{device?.sourceData.device.appSKey}</Typography>
                </Stack>
              }
              <Stack spacing={1}>
                <Typography
                  color="text.secondary"
                  variant="p">
                  Network Server
                </Typography>
                <Typography variant="body2">{device?.sourceData.networkServerId}</Typography>
              </Stack>
              <Stack spacing={1}>
                <Typography
                  color="text.secondary"
                  variant="p">
                  Device Eui
                </Typography>
                <Typography variant="body2">{device?.sourceData.device.devEui}</Typography>
              </Stack>
            </Stack>
            <Stack
              direction={'row'}
              spacing={15}>
              <Stack spacing={1}>
                <Typography
                  color="text.secondary"
                  variant="p">
                  {t('devices.loraType')}
                </Typography>
                <Typography variant="body2">{typeToString(device?.sourceData.device.type)}</Typography>
              </Stack>
              {typeToString(device?.sourceData.device.type) == 'OTAA' ?
                null
                :
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    variant="p">
                    NwkSKey
                  </Typography>
                  <Typography variant="body2">{device?.sourceData.device.nwkSKey}</Typography>
                </Stack>}
              {typeToString(device?.sourceData.device.type) == 'OTAA' ?
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    variant="p">
                    Join Eui
                  </Typography>
                  <Typography variant="body2">{device?.sourceData.device.joinEui}</Typography>
                </Stack>
                :
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    variant="p">
                    Devaddr
                  </Typography>
                  <Typography variant="body2">{device?.sourceData.device.devaddr}</Typography>
                </Stack>}
            </Stack>
          </Stack>
        </Box>
      )}

      <Typography
        mt={5}
        mb={2}
        variant="title">
        {t('devices.categories')}
      </Typography>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}>
        <Card
          variant="standard"
          sx={{
            width: '100%',
            background: 'rgba(179, 126, 221, 0.1)'
          }}>
          <CardContent>
            <Typography sx={{ fontSize: 10 }}>{t('devices.board')}</Typography>
            <Typography sx={{
              fontSize: 14,
              fontWeight: 500
            }}>{device?.device?.public ? t('devices.public') : t('devices.private')}</Typography>
          </CardContent>
        </Card>
        <Card
          variant="standard"
          sx={{
            width: '100%',
            background: 'rgba(136, 204, 211, 0.1)'
          }}>
          <CardContent>
            <Typography sx={{ fontSize: 10 }}>{t('devices.visibility')}</Typography>
            <Typography sx={{
              fontSize: 14,
              fontWeight: 500
            }}>{device?.device?.isHidden ? t('devices.hidden') : t('devices.visible')}</Typography>
          </CardContent>
        </Card>
        <Card
          variant="standard"
          sx={{
            width: '100%',
            background: 'rgba(255, 165, 0, 0.1)'
          }}>
          <CardContent>
            <Typography sx={{ fontSize: 10 }}>{t('devices.group')}</Typography>
            <Typography sx={{
              fontSize: 14,
              fontWeight: 500
            }}>{device?.device?.group}</Typography>
          </CardContent>
        </Card>
        <Card
          variant="standard"
          sx={{
            width: '100%',
            background: 'rgba(229, 94, 194, 0.1)'
          }}>
          <CardContent>
            <Typography sx={{ fontSize: 10 }}>{t('common.type')}</Typography>
            <Typography sx={{
              fontSize: 14,
              fontWeight: 500
            }}>{device?.device?.type}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Typography
        mt={5}
        mb={2}
        variant="title">
        {t('devices.tags')}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        mb={5}>
        {device?.device?.tags?.map((t) => (
          <Chip
            label={t}
            key={t}
            color="primary"
          />
        ))}
      </Stack>

      <Divider />

      <Stack
        direction="column"
        justifyContent="right"
        alignItems="end">
        <Button disabled={disableRefresh} aria-label={`${t('common.recharge')}`} onClick={handleRefreshGraphs}>
          <RefreshRoundedIcon />
        </Button>
        {disableRefresh && (<Typography variant='body2'>Attendere {secondsToWait} secondi</Typography>)}
      </Stack>

      <TabContext value={activeTab}>
        <Stack
          direction="row"
          spacing={1}
          mt={4}>
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <TabList
              onChange={handleChangeTab}
              aria-label="device data tabs">
              <Tab
                aria-label={`tab ${t('common.measures')}`}
                label={t('common.measures')}
                value="measures"
              />

              {canReadDevices && (
                <Tab
                  aria-label={`tab ${t('common.events')}`}
                  label={t('common.events')}
                  value="events"
                />
              )}

              {showNetworkTab && (
                <Tab
                  aria-label={`tab ${t('common.network')}`}
                  data-testid="tab-network"
                  label={t('common.network')}
                  value="network"
                />
              )}
            </TabList>
          </Box>
        </Stack>

        <TabPanel
          value="measures">
          <MeasuresTab
            chart={measuresData}
            quantities={device.quantities}
            device={device.device}
          />
        </TabPanel>

        <TabPanel
          value="events">
          <EventsTab
            events={events}
            allEvents={allEvents}
            onPageChange={setEventsPage}
            paging={{
              page: eventsPage,
              pageSize: EVENTS_PAGE_SIZE
            }}
          />
        </TabPanel>
        <TabPanel
          value="network"
        >
          <NetworkTab
            device={device.device}
            sourceData={device.sourceData}
            statisticsChart={statisticsChart}
            networkChart={networkChart}
          />
        </TabPanel>
      </TabContext>

      {trackerId && (
        <PositionHistoryMap
          positions={positions}
          isLoading={isPositionsLoading}
          initialPosition={device.device.position}
        />
      )}

      {!isDisabled && <Can action="devices_u">
        {isEditDeviceDialogOpen && (
          <UpdateDevice
            deviceId={device.device.id}
            isOpen={isEditDeviceDialogOpen}
            setIsOpen={handleSetIsOpenEditDevice}
          />
        )}
      </Can>}
      {isSuperAdmin && device && (
        <ConfirmWithCheckDialog
          isOpen={isHardDeleteConfirmOpen}
          icon={<ClearIcon fontSize="large" color="error" />}
          onClose={() => setIsHardDeleteConfirmOpen(false)}
          onConfirmAction={handleConfirmHardDelete}
          questionText={t('devices.hardDeleteDevice.title')}
          subQuestionText={device?.device.name}
          copyText={device?.device.id}
          bodyText={t('devices.hardDeleteDevice.subtitle')}
          inputSuggestionText={t('devices.hardDeleteDevice.helper')}
        />
      )}

      <Can
        action="devices_d"
        expression={() => !!device && !isDisabled}>
        <ConfirmWithCheckDialog
          isOpen={isConfirmWithCheckOpen}
          icon={
            <DeleteRoundedIcon
              size={60}
              color="#ef5350"
            />
          }
          onClose={handleCloseConfigrWithCheck}
          onConfirmAction={handleConfirmAction}
          questionText={t('devices.deleteDevice.title')}
          subQuestionText={device?.device.name}
          copyText={device?.device.id}
          bodyText={t('devices.deleteDevice.subtitle')}
          inputSuggestionText={t('devices.deleteDevice.helper')}
        />
      </Can>

      <Can
        action="devices_u"
        expression={() => !!device && !isDisabled}>
        {isActionDialogOpen && (
          <Actions
            onSendAction={handleSendAction}
            isOpen={isActionDialogOpen}
            setIsOpen={handleSetIsOpenActionDialogOpen}
            device={device.device}
          />
        )}
        {isSendActionOpen && (
          <SendAction
            device={device.device}
            isOpen={isSendActionOpen}
            setIsOpen={handleSendActionIsOpen}
          />
        )}
      </Can>
    </>
  )
}

export default DeviceDetail
